import { useCallback, useEffect, useRef } from 'react';
import { useMap } from '../../lib/react-leaflet-lite';

// ─────────────────────────────────────────────────────────────────────────────
// Encuadre inicial de los mapas operativos.
//
// POR QUÉ EXISTE. Los mapas de la app arrancaban con el centro de Santiago
// hardcodeado, así que un operador de Valparaíso, Mendoza o Buenos Aires abría la
// vista sobre una ciudad que no es la suya. Y no alcanza con calcular un `center`
// mejor y pasarlo como prop: el <MapContainer> crea el mapa UNA vez y
// `center`/`zoom` son inertes después (ver lib/react-leaflet-lite.jsx), mientras
// que el encuadre correcto solo se conoce más tarde — los datos del día llegan por
// polling y el país por el catálogo. Por eso se aplica imperativamente.
//
// PRIORIDAD. La operación del día manda; las instalaciones declaradas en
// /admin/settings?tab=locations son el respaldo para cuando no hay nada que
// mostrar (cliente recién dado de alta, día sin ruta, jornada ya cerrada).
// ─────────────────────────────────────────────────────────────────────────────

const FRAME_PADDING = 40;
/** Nunca ceder más que esta fracción del viewport a un panel flotante. */
const MAX_OBSTRUCTION_RATIO = 0.45;

/**
 * Padding que deja libre un panel flotante, MEDIDO del DOM en vez de duplicar los
 * valores del SCSS: estos paneles son laterales en escritorio y hojas inferiores
 * en mobile, y además se colapsan.
 */
export function framePadding(map, obstructionSelector) {
  const base = [FRAME_PADDING, FRAME_PADDING];
  const fallback = { paddingTopLeft: base, paddingBottomRight: base };

  const el = obstructionSelector ? document.querySelector(obstructionSelector) : null;
  if (!el) return fallback;

  const mapRect = map.getContainer().getBoundingClientRect();
  const obs = el.getBoundingClientRect();
  if (!obs.width || !obs.height || !mapRect.width || !mapRect.height) return fallback;

  // El panel está pegado a un borde: el eje en el que tapa MENOS es el eje real de
  // la obstrucción. Un panel lateral derecho tapa ~360px a la derecha y casi todo
  // el alto; una hoja inferior, al revés.
  const overlapRight = Math.max(0, mapRect.right - obs.left);
  const overlapBottom = Math.max(0, mapRect.bottom - obs.top);

  const bottomRight =
    overlapRight <= overlapBottom
      ? [Math.min(overlapRight, mapRect.width * MAX_OBSTRUCTION_RATIO) + FRAME_PADDING, FRAME_PADDING]
      : [FRAME_PADDING, Math.min(overlapBottom, mapRect.height * MAX_OBSTRUCTION_RATIO) + FRAME_PADDING];

  return { paddingTopLeft: base, paddingBottomRight: bottomRight };
}

/**
 * Encuadra el mapa la primera vez, sin robarle la vista al operador.
 *
 * DETECCIÓN DE INTENCIÓN DEL USUARIO. Se escuchan `dragstart` y `zoomstart` con
 * una bandera que descarta los movimientos propios. Deliberadamente NO se usa
 * `e.originalEvent` para distinguirlos: en Leaflet 1.9 esos eventos se emiten sin
 * payload (`fire('movestart')` pelado) y `originalEvent` solo se inyecta en los
 * eventos DOM proxeados, que no los incluyen — una guarda escrita así no se activa
 * nunca. Como los encuadres de acá van con `animate: false`, se ejecutan de forma
 * síncrona y la bandera se puede levantar y bajar alrededor de la llamada.
 *
 * Que `zoomstart` cuente como intención del usuario es a propósito: si otro
 * controlador de la vista mueve la cámara (enfocar un repartidor, abrir un
 * recorrido), fue porque el usuario lo pidió, y este componente debe cederle el
 * paso en vez de pelearle el encuadre.
 *
 * @param {object|null} props.frame salida de `useOperationFraming()`; `null`
 *   significa "todavía no se sabe dónde opera", no "no hay encuadre".
 * @param {Array<[number, number]>} props.dataPoints puntos de la operación del día
 * @param {boolean} [props.enabled] permite cederle el mapa a otro controlador
 * @param {string} [props.obstructionSelector] panel flotante que no debe tapar datos
 * @param {number} [props.maxZoom] tope al encuadrar varios puntos
 * @param {number} [props.singlePointZoom] zoom cuando hay un único punto de datos
 */
export default function InitialFraming({
  frame,
  dataPoints,
  enabled = true,
  obstructionSelector,
  maxZoom = 15,
  singlePointZoom = 14,
}) {
  const map = useMap();
  const programmaticRef = useRef(false);
  const stateRef = useRef({ seeded: false, fittedToData: false, userTookOver: false });

  // Los puntos cambian de identidad en cada poll y en cada snapshot GPS; se leen de
  // una ref para que el efecto dependa de si HAY datos, no de la identidad del array.
  const pointsRef = useRef(dataPoints);
  pointsRef.current = dataPoints;

  useEffect(() => {
    const onUserGesture = () => {
      if (!programmaticRef.current) stateRef.current.userTookOver = true;
    };
    map.on('dragstart', onUserGesture);
    map.on('zoomstart', onUserGesture);
    return () => {
      map.off('dragstart', onUserGesture);
      map.off('zoomstart', onUserGesture);
    };
  }, [map]);

  const apply = useCallback((move) => {
    programmaticRef.current = true;
    try {
      move();
    } finally {
      programmaticRef.current = false;
    }
  }, []);

  const hasData = dataPoints.length > 0;

  useEffect(() => {
    const state = stateRef.current;
    if (!enabled || state.userTookOver || state.fittedToData) return;

    const padding = framePadding(map, obstructionSelector);

    if (hasData) {
      const points = pointsRef.current;
      // Se marca ANTES de mover: el encuadre a datos es definitivo, y si la
      // llamada fallara igual no queremos reintentarla en cada poll.
      state.fittedToData = true;
      state.seeded = true;
      apply(() => {
        if (points.length === 1) {
          map.setView(points[0], singlePointZoom, { animate: false });
        } else {
          map.fitBounds(points, { ...padding, maxZoom, animate: false });
        }
      });
      return;
    }

    // Sin operación visible: sembrar dónde trabaja el cliente. Se hace una sola
    // vez, pero NO cierra la puerta a encuadrar los datos cuando lleguen.
    if (state.seeded || !frame) return;
    state.seeded = true;
    apply(() => {
      if (frame.bounds) {
        map.fitBounds(frame.bounds, { ...padding, maxZoom: 13, animate: false });
      } else {
        map.setView(frame.center, frame.zoom, { animate: false });
      }
    });
  }, [map, frame, hasData, enabled, obstructionSelector, maxZoom, singlePointZoom, apply]);

  return null;
}
